/**
 * Safe storage utilities for Safari/iOS compatibility.
 *
 * Safari can throw QuotaExceededError or SecurityError when:
 * - Storage quota is exceeded (~5MB on mobile)
 * - Private/incognito mode has restricted storage
 * - User has disabled storage for the site
 *
 * These wrappers never throw - they fail gracefully with fallbacks.
 */

/**
 * Safely get an item from localStorage. Returns null if storage is unavailable or throws.
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safely set an item in localStorage. Silently fails if storage is unavailable.
 */
export function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Silently fail - storage may be full or restricted (e.g. Safari private mode)
  }
}

/**
 * Safely remove an item from localStorage. Silently fails if storage is unavailable.
 */
export function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently fail
  }
}

export function safeSessionGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSessionSetItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Same failure modes as localStorage on strict browsers
  }
}

export function safeSessionRemoveItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Silently fail
  }
}
